import { c as _c } from "react/compiler-runtime";
import { IcoInfo24 } from '@onefootprint/icons';
import type { OnboardingRequirementCollectDocument } from '@onefootprint/request-types';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IDV_BODY_CONTENT_CONTAINER_ID } from '@/idv2/components/constants';
import { getLogger } from '@/idv2/utils/logger';
import useProcessImage, { isProcessingResultError } from '../../hooks/use-process-image';
import type { CameraErrorCause, CaptureKind, ReceivedImagePayload } from '../../types';
import { isDesktop, isDocument, isFace } from '../../utils/capture';
import Camera, { type ChildrenProps } from '../camera';
import AutoCaptureDoc from '../camera/auto-capture-doc';
import AutoCaptureFace from '../camera/auto-capture-face';
import type { AutoCaptureKind, CaptureStatus, DeviceKind } from '../camera/types';
import type { CameraSide } from '../camera/utils/get-camera-options';
import type { NavigationHeaderLeftButtonProps } from '../navigation-header';
import NavigationHeader from '../navigation-header';
import Instructions from './components/instructions';
import Preview from './components/preview';
type HeaderTextType = {
  camera: string;
  preview: string;
};
type PhotoCaptureProps = {
  autocaptureKind: AutoCaptureKind;
  autocapture?: boolean;
  cameraKind: CameraSide;
  deviceKind: DeviceKind;
  allowPdf: boolean;
  sideName?: string;
  docName?: string;
  requirement: OnboardingRequirementCollectDocument;
  hasBadConnectivity?: boolean;
  onCameraStuck?: () => void;
  onCameraErrored?: (cause: CameraErrorCause) => void;
  onCameraAccessDenied?: () => void;
  onBack?: () => void;
  onComplete: (payload: ReceivedImagePayload) => void;
  outlineHeightRatio: number; // with respect to the video width (not height)
  outlineWidthRatio: number; // with respect to the video width
  subtitle?: Partial<HeaderTextType>;
  title: HeaderTextType;
};
const DesktopNavProps: NavigationHeaderLeftButtonProps = {
  variant: 'close',
  confirmClose: true
};
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'photo-capture'
});
const SheetContent = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="absolute bottom-10 left-0 right-0 z-10"><div className="bg-primary rounded-t-2xl p-6 shadow-xl">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const FallbackSheet = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="absolute bottom-0 left-0 right-0 z-10"><div className="bg-primary rounded-t-2xl p-6 shadow-xl">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PhotoCapture = t0 => {
  const $ = _c(97);
  const {
    autocaptureKind,
    autocapture: t1,
    cameraKind,
    deviceKind,
    allowPdf,
    sideName,
    docName,
    requirement,
    hasBadConnectivity,
    onCameraStuck,
    onCameraErrored,
    onCameraAccessDenied,
    onBack,
    onComplete,
    outlineHeightRatio,
    outlineWidthRatio,
    subtitle,
    title
  } = t0;
  const autocapture = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "document-flow.components.photo-capture"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t2);
  const allowUpload = requirement.uploadSettings !== "capture_only_on_mobile" && requirement.uploadSettings !== "require_mobile_capture";
  let t3;
  if ($[1] !== allowPdf) {
    t3 = {
      allowPdf
    };
    $[1] = allowPdf;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    processImageUrl
  } = useProcessImage(t3);
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [captureKind, setCaptureKind] = useState();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [autocaptureFeedback, setAutocaptureFeedback] = useState("detecting");
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      logInfo("Retaking photo");
      setImage(null);
      setIsCaptured(false);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleRetake = t4;
  let t5;
  if ($[4] !== captureKind || $[5] !== hasBadConnectivity || $[6] !== image || $[7] !== onComplete || $[8] !== processImageUrl) {
    t5 = async () => {
      if (!image) {
        logWarn("Captured image could not be confirmed and submitted - retaking the image");
        return;
      }
      if (!captureKind) {
        logWarn("Captured kind could not be determined - retaking the image");
        return;
      }
      setIsLoading(true);
      const processResult = await processImageUrl(image, hasBadConnectivity);
      if (isProcessingResultError(processResult)) {
        setIsLoading(false);
        handleRetake();
        logWarn("Captured image could not be processed - retaking the image");
        return;
      }
      setIsLoading(false);
      const {
        file,
        extraCompressed
      } = processResult;
      onComplete({
        captureKind,
        extraCompressed,
        imageFile: file,
        forcedUpload: false
      });
    };
    $[4] = captureKind;
    $[5] = hasBadConnectivity;
    $[6] = image;
    $[7] = onComplete;
    $[8] = processImageUrl;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleConfirm = t5;
  let t6;
  if ($[10] !== onCameraErrored) {
    t6 = cause => {
      logInfo("Camera error occurred", {
        cause
      });
      onCameraErrored?.(cause);
    };
    $[10] = onCameraErrored;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleError = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (newImage, newCaptureKind) => {
      setImage(newImage);
      setCaptureKind(newCaptureKind);
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleCapture = t7;
  let t8;
  if ($[13] !== onCameraStuck) {
    t8 = () => {
      onCameraStuck?.();
      logInfo("Photocapture: Camera stuck, fallback to doc upload");
    };
    $[13] = onCameraStuck;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleCameraStuck = t8;
  let t9;
  if ($[15] !== autocapture || $[16] !== autocaptureKind || $[17] !== isCaptured) {
    t9 = t10 => {
      const {
        canvasAutoCaptureRef,
        feedbackPositionFromBottom,
        videoResolution,
        onDetectionComplete,
        onDetectionReset,
        outlineHeight,
        outlineWidth,
        videoRef,
        videoSize
      } = t10;
      if (!autocapture) {
        return null;
      }
      if (isDocument(autocaptureKind)) {
        return <AutoCaptureDoc canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} videoResolution={videoResolution} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineHeight={outlineHeight} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} />;
      }
      return isFace(autocaptureKind) ? <AutoCaptureFace canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} videoResolution={videoResolution} /> : null;
    };
    $[15] = autocapture;
    $[16] = autocaptureKind;
    $[17] = isCaptured;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const renderCameraChildren = t9;
  let t10;
  if ($[19] !== deviceKind) {
    t10 = isDesktop(deviceKind);
    $[19] = deviceKind;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const desktop = t10;
  const showOverlay = !!(image || isLoading);
  let t11;
  let t12;
  if ($[21] !== showOverlay) {
    t11 = () => {
      if (!showOverlay) {
        return;
      }
      const target = document.getElementById(IDV_BODY_CONTENT_CONTAINER_ID)?.parentElement;
      if (target && getComputedStyle(target).position === "static") {
        target.style.position = "relative";
      }
    };
    t12 = [showOverlay];
    $[21] = showOverlay;
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  let t15;
  let t16;
  if ($[24] !== allowPdf || $[25] !== allowUpload || $[26] !== autocapture || $[27] !== autocaptureFeedback || $[28] !== autocaptureKind || $[29] !== cameraKind || $[30] !== desktop || $[31] !== deviceKind || $[32] !== docName || $[33] !== handleCameraStuck || $[34] !== handleConfirm || $[35] !== handleError || $[36] !== hasBadConnectivity || $[37] !== image || $[38] !== isCaptured || $[39] !== isLoading || $[40] !== onBack || $[41] !== onCameraAccessDenied || $[42] !== onComplete || $[43] !== outlineHeightRatio || $[44] !== outlineWidthRatio || $[45] !== renderCameraChildren || $[46] !== showOverlay || $[47] !== sideName || $[48] !== subtitle || $[49] !== t || $[50] !== title.camera) {
    const sheetPortalTarget = showOverlay ? document.getElementById(IDV_BODY_CONTENT_CONTAINER_ID)?.parentElement ?? null : null;
    if ($[55] !== desktop || $[56] !== onBack || $[57] !== subtitle || $[58] !== title.camera) {
      t16 = desktop ? <div className="mb-6"><NavigationHeader leftButton={DesktopNavProps} /><div className="flex flex-col items-center gap-2 text-center"><h2 className="text-heading-3 text-primary">{title.camera}</h2>{subtitle?.camera && <p className="text-body-2 text-secondary">{subtitle.camera}</p>}</div></div> : <NavigationHeader leftButton={{
        variant: "back",
        onBack
      }} rightButton={{
        icon: IcoInfo24,
        onClick: () => setShowInstructions(true)
      }} content={{
        kind: "static",
        title: title.camera
      }} />;
      $[55] = desktop;
      $[56] = onBack;
      $[57] = subtitle;
      $[58] = title.camera;
      $[59] = t16;
    } else {
      t16 = $[59];
    }
    t13 = "relative w-full flex-1 min-h-0";
    const t17 = showOverlay && "blur-sm pointer-events-none";
    let t18;
    if ($[60] !== t17) {
      t18 = cx("w-full h-full transition-[filter]", t17);
      $[60] = t17;
      $[61] = t18;
    } else {
      t18 = $[61];
    }
    let t19;
    if ($[62] !== allowPdf || $[63] !== allowUpload || $[64] !== autocapture || $[65] !== autocaptureFeedback || $[66] !== autocaptureKind || $[67] !== cameraKind || $[68] !== deviceKind || $[69] !== docName || $[70] !== handleCameraStuck || $[71] !== handleError || $[72] !== hasBadConnectivity || $[73] !== isCaptured || $[74] !== onCameraAccessDenied || $[75] !== onComplete || $[76] !== outlineHeightRatio || $[77] !== outlineWidthRatio || $[78] !== renderCameraChildren || $[79] !== sideName) {
      t19 = <Camera autocapture={autocapture} autoCaptureFeedback={autocaptureFeedback} autoCaptureKind={autocaptureKind} cameraSide={cameraKind} deviceKind={deviceKind} docName={docName} sideName={sideName} onCapture={handleCapture} onUpload={onComplete} hasBadConnectivity={hasBadConnectivity} onCameraError={handleError} onCameraAccessDenied={onCameraAccessDenied} outlineHeightRatio={outlineHeightRatio} outlineWidthRatio={outlineWidthRatio} isCaptured={isCaptured} setIsCaptured={setIsCaptured} allowPdf={allowPdf} onCameraStuck={handleCameraStuck} allowUpload={allowUpload}>{renderCameraChildren}</Camera>;
      $[62] = allowPdf;
      $[63] = allowUpload;
      $[64] = autocapture;
      $[65] = autocaptureFeedback;
      $[66] = autocaptureKind;
      $[67] = cameraKind;
      $[68] = deviceKind;
      $[69] = docName;
      $[70] = handleCameraStuck;
      $[71] = handleError;
      $[72] = hasBadConnectivity;
      $[73] = isCaptured;
      $[74] = onCameraAccessDenied;
      $[75] = onComplete;
      $[76] = outlineHeightRatio;
      $[77] = outlineWidthRatio;
      $[78] = renderCameraChildren;
      $[79] = sideName;
      $[80] = t19;
    } else {
      t19 = $[80];
    }
    if ($[81] !== t18 || $[82] !== t19) {
      t14 = <div className={t18}>{t19}</div>;
      $[81] = t18;
      $[82] = t19;
      $[83] = t14;
    } else {
      t14 = $[83];
    }
    t15 = image && (sheetPortalTarget ? createPortal(<SheetContent><Preview imageSrc={image} onRetake={handleRetake} onConfirm={handleConfirm} isLoading={isLoading} cameraKind={cameraKind} deviceKind={deviceKind} title={isDocument(autocaptureKind) ? t(_temp) : t(_temp2)} /></SheetContent>, sheetPortalTarget) : <FallbackSheet><Preview imageSrc={image} onRetake={handleRetake} onConfirm={handleConfirm} isLoading={isLoading} cameraKind={cameraKind} deviceKind={deviceKind} title={isDocument(autocaptureKind) ? t(_temp3) : t(_temp4)} /></FallbackSheet>);
    $[24] = allowPdf;
    $[25] = allowUpload;
    $[26] = autocapture;
    $[27] = autocaptureFeedback;
    $[28] = autocaptureKind;
    $[29] = cameraKind;
    $[30] = desktop;
    $[31] = deviceKind;
    $[32] = docName;
    $[33] = handleCameraStuck;
    $[34] = handleConfirm;
    $[35] = handleError;
    $[36] = hasBadConnectivity;
    $[37] = image;
    $[38] = isCaptured;
    $[39] = isLoading;
    $[40] = onBack;
    $[41] = onCameraAccessDenied;
    $[42] = onComplete;
    $[43] = outlineHeightRatio;
    $[44] = outlineWidthRatio;
    $[45] = renderCameraChildren;
    $[46] = showOverlay;
    $[47] = sideName;
    $[48] = subtitle;
    $[49] = t;
    $[50] = title.camera;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
  } else {
    t13 = $[51];
    t14 = $[52];
    t15 = $[53];
    t16 = $[54];
  }
  let t17;
  if ($[84] !== t13 || $[85] !== t14 || $[86] !== t15) {
    t17 = <div className={t13}>{t14}{t15}</div>;
    $[84] = t13;
    $[85] = t14;
    $[86] = t15;
    $[87] = t17;
  } else {
    t17 = $[87];
  }
  let t18;
  if ($[88] !== autocapture || $[89] !== autocaptureKind || $[90] !== desktop || $[91] !== showInstructions) {
    t18 = !desktop && <Instructions onClose={() => setShowInstructions(false)} isOpen={showInstructions} autoCaptureKind={autocaptureKind} autocapture={autocapture} />;
    $[88] = autocapture;
    $[89] = autocaptureKind;
    $[90] = desktop;
    $[91] = showInstructions;
    $[92] = t18;
  } else {
    t18 = $[92];
  }
  let t19;
  if ($[93] !== t16 || $[94] !== t17 || $[95] !== t18) {
    t19 = <>{t16}{t17}{t18}</>;
    $[93] = t16;
    $[94] = t17;
    $[95] = t18;
    $[96] = t19;
  } else {
    t19 = $[96];
  }
  return t19;
};
export default PhotoCapture;
function _temp($) {
  return $.preview.titleDocument;
}
function _temp2($_0) {
  return $_0.preview.titleFace;
}
function _temp3($_1) {
  return $_1.preview.titleDocument;
}
function _temp4($_2) {
  return $_2.preview.titleFace;
}